import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  TodoTask,
  TodoState,
  TaskStatus,
  TaskPriority,
  CompletedBatch,
  CompletedProduct,
  InventoryItem
} from '../../types/todo';
import { v4 as uuidv4 } from 'uuid';

// Helper function to load tasks from localStorage
const loadTasksFromStorage = (): TodoTask[] => {
  try {
    const stored = localStorage.getItem('todo-tasks');
    if (!stored) return [];

    let tasks = JSON.parse(stored);

    // Migration: Convert orderId to scheduledOrderIds
    tasks = tasks.map((task: any) => {
      if (task.orderId && !task.scheduledOrderIds) {
        console.warn('[Migration] Converting task orderId to scheduledOrderIds:', task.id);
        return {
          ...task,
          scheduledOrderIds: [task.orderId],
          orderId: undefined
        };
      }
      return task;
    });

    return tasks;
  } catch (error) {
    console.error('Error loading todo tasks from localStorage:', error);
    return [];
  }
};

// Helper function to load completed batches from localStorage
const loadCompletedBatchesFromStorage = () => {
  try {
    const stored = localStorage.getItem('completed-batches');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading completed batches from localStorage:', error);
    return [];
  }
};

// Helper function to load completed products from localStorage
const loadCompletedProductsFromStorage = () => {
  try {
    const stored = localStorage.getItem('completed-products');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading completed products from localStorage:', error);
    return [];
  }
};

// Helper function to load raw ingredients from localStorage
const loadRawIngredientsFromStorage = (): InventoryItem[] => {
  try {
    const stored = localStorage.getItem('raw-ingredients');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading raw ingredients from localStorage:', error);
    return [];
  }
};

// Helper function to load supplies from localStorage
const loadSuppliesFromStorage = (): InventoryItem[] => {
  try {
    const stored = localStorage.getItem('supplies');
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    console.error('Error loading supplies from localStorage:', error);
    return [];
  }
};

// Helper function to create a blank task
export const createBlankTask = (): TodoTask => {
  const now = new Date().toISOString();

  return {
    id: uuidv4(),
    title: '',
    description: '',
    status: 'pending',
    priority: 'medium',
    createdAt: now,
    updatedAt: now
  };
};

const initialState: TodoState = {
  tasks: loadTasksFromStorage(),
  completedBatches: loadCompletedBatchesFromStorage(),
  completedProducts: loadCompletedProductsFromStorage(),
  rawIngredients: loadRawIngredientsFromStorage(),
  supplies: loadSuppliesFromStorage(),
  filter: {
    status: 'all',
    priority: 'all',
    searchQuery: ''
  },
  sortBy: 'dueDate',
  sortOrder: 'asc'
};

const todoSlice = createSlice({
  name: 'todo',
  initialState,
  reducers: {
    addTask: (state, action: PayloadAction<TodoTask>) => {
      state.tasks.push(action.payload);
    },
    updateTask: (state, action: PayloadAction<TodoTask>) => {
      const index = state.tasks.findIndex(task => task.id === action.payload.id);
      if (index !== -1) {
        state.tasks[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString()
        };
      }
    },
    deleteTask: (state, action: PayloadAction<string>) => {
      state.tasks = state.tasks.filter(task => task.id !== action.payload);
    },
    updateTaskStatus: (state, action: PayloadAction<{ id: string; status: TaskStatus }>) => {
      const task = state.tasks.find(t => t.id === action.payload.id);
      if (task) {
        task.status = action.payload.status;
        task.updatedAt = new Date().toISOString();
        if (action.payload.status === 'completed') {
          task.completedAt = new Date().toISOString();
        } else {
          task.completedAt = undefined;
        }
      }
    },
    updateTaskPriority: (state, action: PayloadAction<{ id: string; priority: TaskPriority }>) => {
      const task = state.tasks.find(t => t.id === action.payload.id);
      if (task) {
        task.priority = action.payload.priority;
        task.updatedAt = new Date().toISOString();
      }
    },
    setFilterStatus: (state, action: PayloadAction<TaskStatus | 'all'>) => {
      state.filter.status = action.payload;
    },
    setFilterPriority: (state, action: PayloadAction<TaskPriority | 'all'>) => {
      state.filter.priority = action.payload;
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.filter.searchQuery = action.payload;
    },
    setSortBy: (state, action: PayloadAction<'dueDate' | 'priority' | 'createdAt' | 'status'>) => {
      state.sortBy = action.payload;
    },
    setSortOrder: (state, action: PayloadAction<'asc' | 'desc'>) => {
      state.sortOrder = action.payload;
    },
    toggleSortOrder: state => {
      state.sortOrder = state.sortOrder === 'asc' ? 'desc' : 'asc';
    },
    clearFilters: state => {
      state.filter = {
        status: 'all',
        priority: 'all',
        searchQuery: ''
      };
    },
    addCompletedBatch: (state, action: PayloadAction<CompletedBatch>) => {
      state.completedBatches.push(action.payload);
    },
    addCompletedProduct: (state, action: PayloadAction<CompletedProduct>) => {
      state.completedProducts.push(action.payload);
    },
    deleteCompletedBatchesByFilling: (state, action: PayloadAction<string>) => {
      state.completedBatches = state.completedBatches.filter(batch => batch.fillingName !== action.payload);
    },
    completeTaskWithBatch: (state, action: PayloadAction<{ taskId: string }>) => {
      const task = state.tasks.find(t => t.id === action.payload.taskId);
      if (task) {
        // Mark task as completed
        task.status = 'completed';
        task.updatedAt = new Date().toISOString();
        task.completedAt = new Date().toISOString();

        // If task is a batch prep task, record completed batch
        if (task.taskType === 'batch' && task.fillingName && task.batchesNeeded) {
          const completedBatch: CompletedBatch = {
            id: uuidv4(),
            fillingName: task.fillingName,
            batchCount: task.batchesNeeded,
            completedAt: new Date().toISOString(),
            taskId: task.id
          };
          state.completedBatches.push(completedBatch);
        }

        // If task is an order fulfillment task, record completed product
        if (task.taskType === 'fulfillment' && task.fillingName && task.batchesNeeded) {
          const completedProduct: CompletedProduct = {
            id: uuidv4(),
            productName: task.fillingName,
            unitCount: task.batchesNeeded,
            completedAt: new Date().toISOString(),
            taskId: task.id
          };
          state.completedProducts.push(completedProduct);
        }
      }
    },
    deleteCompletedProductsByName: (state, action: PayloadAction<string>) => {
      state.completedProducts = state.completedProducts.filter(product => product.productName !== action.payload);
    },
    clearCalendarTasks: state => {
      // Remove all tasks that have a dueDate (calendar-scheduled tasks)
      state.tasks = state.tasks.filter(task => !task.dueDate);
    },
    clearAllInventory: state => {
      // Clear all completed batches and products
      state.completedBatches = [];
      state.completedProducts = [];
    },
    deductInventoryForOrder: (
      state,
      action: PayloadAction<{ orderId: string; items: { recipeName: string; quantity: number }[] }>
    ) => {
      const { orderId, items } = action.payload;

      console.log(`[InventoryDeduction] Deducting inventory for order ${orderId}`);

      // For each item in the order, deduct from completed products
      items.forEach(item => {
        let remainingToDeduct = item.quantity;

        // Find all completed products with matching name (FIFO - oldest first)
        const matchingProducts = state.completedProducts
          .map((product, index) => ({ product, index }))
          .filter(({ product }) => product.productName === item.recipeName)
          .sort((a, b) => new Date(a.product.completedAt).getTime() - new Date(b.product.completedAt).getTime());

        // Deduct from products until we've deducted the full quantity
        for (const { product, index } of matchingProducts) {
          if (remainingToDeduct <= 0) break;

          if (product.unitCount <= remainingToDeduct) {
            // Consume entire product
            console.log(
              `[InventoryDeduction] Removing ${product.unitCount} units of ${product.productName} (product ID: ${product.id})`
            );
            remainingToDeduct -= product.unitCount;
            state.completedProducts.splice(index, 1);
          } else {
            // Partially consume product
            console.log(
              `[InventoryDeduction] Deducting ${remainingToDeduct} units from ${product.productName} (product ID: ${product.id})`
            );
            product.unitCount -= remainingToDeduct;
            remainingToDeduct = 0;
          }
        }

        if (remainingToDeduct > 0) {
          console.warn(
            `[InventoryDeduction] Warning: Could not deduct full quantity for ${item.recipeName}. Remaining: ${remainingToDeduct}`
          );
        } else {
          console.log(`[InventoryDeduction] ✓ Successfully deducted ${item.quantity} units of ${item.recipeName}`);
        }
      });
    },
    // Raw Ingredients actions
    addRawIngredient: (state, action: PayloadAction<Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const now = new Date().toISOString();
      const newItem: InventoryItem = {
        id: uuidv4(),
        ...action.payload,
        createdAt: now,
        updatedAt: now
      };
      state.rawIngredients.push(newItem);
    },
    updateRawIngredient: (state, action: PayloadAction<InventoryItem>) => {
      const index = state.rawIngredients.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.rawIngredients[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString()
        };
      }
    },
    deleteRawIngredient: (state, action: PayloadAction<string>) => {
      state.rawIngredients = state.rawIngredients.filter(item => item.id !== action.payload);
    },
    // Supplies actions
    addSupply: (state, action: PayloadAction<Omit<InventoryItem, 'id' | 'createdAt' | 'updatedAt'>>) => {
      const now = new Date().toISOString();
      const newItem: InventoryItem = {
        id: uuidv4(),
        ...action.payload,
        createdAt: now,
        updatedAt: now
      };
      state.supplies.push(newItem);
    },
    updateSupply: (state, action: PayloadAction<InventoryItem>) => {
      const index = state.supplies.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.supplies[index] = {
          ...action.payload,
          updatedAt: new Date().toISOString()
        };
      }
    },
    deleteSupply: (state, action: PayloadAction<string>) => {
      state.supplies = state.supplies.filter(item => item.id !== action.payload);
    }
  }
});

export const {
  addTask,
  updateTask,
  deleteTask,
  updateTaskStatus,
  updateTaskPriority,
  setFilterStatus,
  setFilterPriority,
  setSearchQuery,
  setSortBy,
  setSortOrder,
  toggleSortOrder,
  clearFilters,
  addCompletedBatch,
  addCompletedProduct,
  deleteCompletedBatchesByFilling,
  completeTaskWithBatch,
  deleteCompletedProductsByName,
  clearCalendarTasks,
  clearAllInventory,
  deductInventoryForOrder,
  addRawIngredient,
  updateRawIngredient,
  deleteRawIngredient,
  addSupply,
  updateSupply,
  deleteSupply
} = todoSlice.actions;

export default todoSlice.reducer;
